import { Contact } from '@/modules/core/data/types/Contact';
import { ContactPlaceInfo } from '@/modules/core/data/types/ContactPlaceInfo';
import { ContactSlider } from '@/modules/core/data/types/ContactSlider';
import filter from 'lodash.filter';
import find from 'lodash.find';
import groupBy from 'lodash.groupby';
import isEmpty from 'lodash.isempty';
import map from 'lodash.map';
import size from 'lodash.size';
import some from 'lodash.some';
import uniq from 'lodash.uniq';
import { Office } from '@/modules/core/data/types/Office';

/**
 * Ref used by the Office that doesn't represent an actual office.
 * That "office" stores the contacts that don't belong to any office/place.
 */
export const NO_OFFICE_GROUP_REF = 'no-office';

/**
 * Whether a contact should be displayed, depending on what information are available about him/her.
 *
 * If there are no meaningful information to display, there is no use displaying it.
 *
 * @param contact
 */
export const shouldDisplayContact = (contact: Contact): boolean => {
  const hasContactName = !!contact?.name;
  const hasContactTitle = !!contact?.title;
  const hasSingleOfficePhone = !!contact?.singleOfficePhone;
  const hasSingleOfficeDescription = !!contact?.singleOfficeDescription;
  const hasSingleOfficeBuildingRoomInfo = !!contact?.singleOfficeBuildingRoomInfo;
  const hasMultiOffices = !isEmpty(contact?.multiOffices);
  const hasContactEmail = !!contact?.email;

  return hasContactName ||
    hasContactTitle ||
    hasSingleOfficePhone ||
    hasSingleOfficeDescription ||
    hasSingleOfficeBuildingRoomInfo ||
    hasMultiOffices ||
    hasContactEmail;
};

/**
 * Whether at least one contact amongst a list of contacts should be displayed.
 *
 * @param contacts
 */
export const shouldDisplayAnyContact = (contacts: Contact[]): boolean => {
  return some(contacts, (contact: Contact): boolean => {
    return shouldDisplayContact(contact);
  });
};

type RawOfficeGroup = { [ref: string]: Contact[] };

/**
 * Takes a list of contacts and returns a list of offices with contacts within.
 * Basically group contacts by office, only handle contacts who are available in a single office.
 *
 * @param contacts
 * @param noOfficeGroupRef
 */
export const groupSingleOfficeContactsByOffice = (contacts: Contact[], noOfficeGroupRef = NO_OFFICE_GROUP_REF): Office[] => {
  const offices: Office[] = [];
  const rawOfficesGroups: RawOfficeGroup[] = groupBy(contacts, 'singleOffice.ref') as unknown as RawOfficeGroup[];

  map(rawOfficesGroups, (rawOfficesGroup: RawOfficeGroup, rawOfficesGroupKey: string) => {
    map(rawOfficesGroup, (rawOffice: Contact) => {
      const officeRef = rawOffice?.singleOffice?.ref ? rawOffice?.singleOffice?.ref + '-office' : noOfficeGroupRef;

      // If the office doesn't exist already, then create it
      if (!find(offices, { ref: officeRef })) {
        const office: Office = {
          ref: officeRef,
          isCampus: !!rawOffice?.singleOffice?.campus,
          isDefaultOffice: officeRef === noOfficeGroupRef,
          address: rawOffice?.singleOffice?.address,
          label: rawOffice?.singleOffice?.label,
          contacts: rawOfficesGroups[rawOfficesGroupKey], // Add all contacts that have the same office ref
        };

        offices.push(office);
      }
    });
  });

  return offices;
};

/**
 * Takes a list of contacts and returns a list of offices with contacts within.
 * Basically group contacts by office, only handle contacts who are available in multi office. (even if they are only related to a single office)
 *
 * @param contacts
 * @param noOfficeGroupRef
 */
export const groupMultiOfficesContactsByOffice = (contacts: Contact[], noOfficeGroupRef = NO_OFFICE_GROUP_REF): Office[] => {
  const offices: Office[] = [];

  // Compute list of offices from all contacts
  map(contacts, (contact: Contact) => {
    if (size(contact?.multiOffices)) {
      map(contact?.multiOffices, (contactPlaceInfo: ContactPlaceInfo) => {
        const placeOriginalRef = contactPlaceInfo?.place?.ref;
        const officeRef = contactPlaceInfo?.place?.ref ? contactPlaceInfo?.place?.ref + '-office' : noOfficeGroupRef;

        // If the office doesn't exist already, then create it
        if (!find(offices, { ref: officeRef })) {
          // Find all contacts that are related to the office and add them all
          const contactsInOffice: Contact[] = filter(contacts, (contact: Contact) => {
            let isContactInOffice = false;

            map(contact.multiOffices, (contactPlaceInfo: ContactPlaceInfo) => {
              if (contactPlaceInfo?.place?.ref === placeOriginalRef) {
                isContactInOffice = true;
              }
            });

            return isContactInOffice;
          });

          const office: Office = {
            ref: officeRef,
            isCampus: !!contactPlaceInfo?.place?.campus,
            isDefaultOffice: officeRef === noOfficeGroupRef,
            address: contactPlaceInfo?.place?.address,
            label: contactPlaceInfo?.place?.label,
            // @ts-expect-error Bad TS typings for "uniq"
            contacts: uniq(contactsInOffice, 'ref'), // Remove duplicate contacts by checking duplicated "ref"
          };

          offices.push(office);
        }
      });
    } else {
      const defaultOffice: Office = find(offices, { ref: noOfficeGroupRef });

      // If the default office (for contacts who don't belong to any office) doesn't exist already, then create it
      if (!defaultOffice) {
        const office: Office = {
          ref: noOfficeGroupRef,
          isCampus: false,
          isDefaultOffice: true,
          address: undefined,
          label: undefined,
          contacts: [contact],
        };

        offices.push(office);
      } else {
        // Otherwise, add contact to existing default office, while avoiding duplicates
        map(defaultOffice.contacts, (contact: Contact) => {
          if (!find(defaultOffice?.contacts, { ref: contact?.ref })) {
            defaultOffice?.contacts?.push(contact);
          }
        });
      }
    }
  });

  return offices;
};

/**
 * Takes a list of contacts and returns a list of offices with contacts within.
 * Basically group contacts by office, whether they are in multiOffices or not.
 *
 * @param contacts
 */
export const groupContactsByOffice = (contacts: Contact[]): Office[] => {
  const offices: Office[] = [];

  const mixedOffices: Office[] = [
    ...groupSingleOfficeContactsByOffice(filter(contacts, (contact: Contact) => !contact.isMultiOffices)),
    ...groupMultiOfficesContactsByOffice(filter(contacts, (contact: Contact) => contact.isMultiOffices)),
  ];

  map(mixedOffices, (office: Office) => {
    const officeRef = office.ref;
    const existingOffice: Office = find(offices, { ref: officeRef });

    // If the office doesn't exist already, then create it
    if (!existingOffice) {
      offices.push(office);
    } else {
      // Merge contacts that belong to the same office, while avoiding duplicates
      map(office.contacts, (contact: Contact) => {
        if (!find(existingOffice?.contacts, { ref: contact?.ref })) {
          existingOffice?.contacts?.push(contact);
        }
      });
    }
  });

  return offices;
};

/**
 * Convert an array of Contact into an array of ContactSlider.
 *
 * Handles contact that are related to either one office or several offices.
 * Duplicates all contacts who are related to several offices.
 * In this case, the common (contact-related) properties are duplicated, while the specific office-related properties are copied over.
 *
 * @param contacts
 */
export const flattenContactsToContactsSlider = (contacts: Contact[]): ContactSlider[] => {
  const contactsSlider: ContactSlider[] = [];

  map(contacts, (contact: Contact) => {
    // Copy generic properties that don't depend on whether the contact is single office or multi offices.
    const contactSlider: ContactSlider = {
      id: contact?.id,
      name: contact?.name,
      title: contact?.title,
      email: contact?.email,
      languages: contact?.languages,
    };

    if (!contact?.isMultiOffices) {
      // Simple use-case, the contact only has one office, no duplication
      contactSlider.officeLabel = contact?.singleOffice?.label;
      contactSlider.officeAddress = contact?.singleOffice?.address;
      contactSlider.officeBuildingRoomInfo = contact?.singleOfficeBuildingRoomInfo;
      contactSlider.officeDescription = contact?.singleOfficeDescription;
      contactSlider.officePhone = contact?.singleOfficePhone;

      contactsSlider.push(contactSlider);
    } else {
      // Advanced use-case, the contact is related to several offices, duplicate the contact for each office (create one contactSlider per office)
      map(contact?.multiOffices, (office: ContactPlaceInfo) => {
        contactSlider.officeLabel = office?.place?.label;
        contactSlider.officeAddress = office?.place?.address; // TODO awaiting decision on https://app.asana.com/0/inbox/584852631485915/1199120153849197/1199084726253421
        contactSlider.officeBuildingRoomInfo = office?.buildingRoomInfo;
        contactSlider.officeDescription = office?.description;
        contactSlider.officePhone = office?.phone;

        contactsSlider.push(contactSlider);
      });
    }
  });

  // @ts-ignore
  return uniq(contactsSlider, 'id');
};
